import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { api, errMessage } from "@/lib/api";
import { Loading, ErrorBox } from "@/components/common";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/time";

const KIND = { take5: "Take 5", inspection: "Inspection" };
const THIS = { take5: "ce Take 5", inspection: "cette inspection" };
const DONE = { take5: ["Take 5 supprimé.", "Take 5 restauré."], inspection: ["Inspection supprimée.", "Inspection restaurée."] };

export const DeletedRecords = () => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const load = () => {
    setError("");
    api.get("/admin/deleted-records").then((r) => setRows(r.data)).catch((e) => setError(errMessage(e)));
  };
  useEffect(() => { load(); }, []);
  const restore = async (r) => {
    try {
      await api.post(`/admin/deleted-records/${r.id}/restore`);
      toast.success(DONE[r.kind][1]);
      load();
    } catch (e) { toast.error(errMessage(e)); }
  };
  if (error) return <ErrorBox message={error} onRetry={load} />;
  if (!rows) return <Loading />;
  return (
    <div data-testid="deleted-records">
      <p className="mb-4 text-sm text-muted-foreground">Take 5 et inspections supprimés par un administrateur. Ils n'apparaissent plus nulle part et peuvent être restaurés à l'identique.</p>
      {!rows.length && <p className="text-sm text-muted-foreground" data-testid="deleted-records-empty">La corbeille est vide.</p>}
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} data-testid={`deleted-row-${r.record_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
            <div className="min-w-0">
              <p className="font-semibold">{r.label.startsWith("Take 5") ? r.label : `${KIND[r.kind]} · ${r.label}`}</p>
              <p className="text-xs text-muted-foreground">Supprimé le {fmtDateTime(r.deleted_at)} par {r.deleted_by_name}{r.reason ? ` — ${r.reason}` : ""}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => restore(r)} data-testid={`restore-${r.record_id}`}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Restaurer</Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DeleteRecordButton = ({ kind, id, onDeleted }) => {
  const remove = async () => {
    const reason = window.prompt(`Supprimer ${THIS[kind]} ? Masqué partout, restaurable depuis Administration > Paramètres > Corbeille.\n\nMotif (facultatif) :`, "");
    if (reason === null) return;
    try {
      await api.delete(`/admin/records/${kind}/${id}`, { params: { reason } });
      toast.success(DONE[kind][0]);
      onDeleted();
    } catch (e) { toast.error(errMessage(e)); }
  };
  return <Button variant="ghost" onClick={remove} data-testid={`${kind}-delete-button`} className="text-red-500 hover:bg-red-500/10">Supprimer</Button>;
};
